<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { getI18n } from '@/plugins/i18n'
import VuePdfEmbed from 'vue-pdf-embed'

defineProps({
  source: { type: [Object, String], default: null },
  loading: { type: Boolean, default: false },
})

const i18n = getI18n()
const container = ref(null)
const scrollEl = ref(null)
const baseWidth = ref(null)
const zoomLevel = ref(1)
let resizeObserver = null
let resizeDebounce = null

const effectiveWidth = computed(() => baseWidth.value ? baseWidth.value * zoomLevel.value : null)

const zoomIn = () => { zoomLevel.value = Math.min(+(zoomLevel.value + 0.2).toFixed(1), 3) }
const zoomOut = () => { zoomLevel.value = Math.max(+(zoomLevel.value - 0.2).toFixed(1), 0.4) }

let isPanning = false
let panStart = { x: 0, y: 0, scrollLeft: 0, scrollTop: 0 }

const onMouseDown = (e) => {
  if (e.button !== 0) return
  isPanning = true
  panStart = { x: e.clientX, y: e.clientY, scrollLeft: scrollEl.value.scrollLeft, scrollTop: scrollEl.value.scrollTop }
  scrollEl.value.style.cursor = 'grabbing'
}

const onMouseMove = (e) => {
  if (!isPanning) return
  e.preventDefault()
  scrollEl.value.scrollLeft = panStart.scrollLeft - (e.clientX - panStart.x)
  scrollEl.value.scrollTop = panStart.scrollTop - (e.clientY - panStart.y)
}

const stopPan = () => {
  isPanning = false
  if (scrollEl.value) scrollEl.value.style.cursor = 'grab'
}

onMounted(() => {
  resizeObserver = new ResizeObserver(([entry]) => {
    clearTimeout(resizeDebounce)
    resizeDebounce = setTimeout(() => { baseWidth.value = entry.contentRect.width * 0.9 }, 200)
  })
  if (container.value) resizeObserver.observe(container.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})
</script>

<template>
  <div ref="container" class="relative-position" style="height: 100%;">
    <div v-if="loading" class="absolute-full flex flex-center bg-white" style="z-index: 1;">
      <q-spinner color="primary" size="3rem" />
    </div>
    <div
      v-if="source"
      ref="scrollEl"
      class="absolute-full overflow-auto bg-grey-3 q-pa-md flex flex-center"
      style="cursor: grab; user-select: none;"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="stopPan"
      @mouseleave="stopPan"
    >
      <vue-pdf-embed :source="source" :width="effectiveWidth" style="max-width: 100%;" />
    </div>
    <div v-if="source" class="pdf-zoom-controls">
      <q-btn
        round
        dense
        flat
        icon="mdi-magnify-plus-outline"
        color="grey-8"
        @click="zoomIn"
      />
      <q-btn
        round
        dense
        flat
        icon="mdi-magnify-minus-outline"
        color="grey-8"
        @click="zoomOut"
      />
    </div>
    <div v-else-if="!loading" class="absolute-full flex flex-center text-grey-5 text-center">
      <div>
        <q-icon name="mdi-file-image-outline" size="4rem" />
        <div class="q-mt-sm">{{ i18n.t('common.preview') }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pdf-zoom-controls {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
</style>

<style>
.vue-pdf-embed__page {
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
